import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  House,
  Users,
  Storefront,
  MagnifyingGlass,
  ChartBar,
  CreditCard,
  Heartbeat,
  Gear,
  SignOut,
  List,
  X,
  Crown,
  ArrowLeft,
  AddressBook
} from "@phosphor-icons/react";

const NAV = [
  { path: "/admin", label: "Dashboard", icon: House },
  { path: "/admin/owners", label: "Owners", icon: Users },
  { path: "/admin/outlets", label: "Outlets", icon: Storefront },
  { path: "/admin/activity", label: "Activity", icon: MagnifyingGlass },
  { path: "/admin/analytics", label: "Analytics", icon: ChartBar },
  { path: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard },
  { path: "/admin/leads", label: "Leads", icon: AddressBook },
  { path: "/admin/health", label: "System Health", icon: Heartbeat },
  { path: "/admin/settings", label: "Settings", icon: Gear },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-white border-r-2 flex flex-col transition-transform duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        style={{ borderColor: "var(--border)" }}
      >
        <div className="h-16 flex items-center gap-3 px-5 border-b-2" style={{ borderColor: "var(--border)" }}>
          <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-orange-500 text-white font-black shadow-md">
            <Crown size={20} weight="fill" />
          </div>
          <div>
            <span className="font-black text-lg leading-tight">Rating Ally</span>
            <div className="text-[10px] font-bold uppercase tracking-widest text-orange-500">Super Admin</div>
          </div>
          <button className="lg:hidden ml-auto" onClick={() => setMobileOpen(false)}>
            <X size={20} />
          </button>
        </div>

        {/* Back to App */}
        <div className="px-3 pt-2 pb-1">
          <Link
            to="/dashboard"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold text-[var(--ink-muted)] hover:bg-gray-50 hover:text-[var(--ink)] border border-dashed border-[var(--border)]"
          >
            <ArrowLeft size={16} />
            Back to App
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {NAV.map((item) => {
            const active = loc.pathname === item.path || (item.path !== "/admin" && loc.pathname.startsWith(item.path));
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  active
                    ? "bg-orange-50 text-orange-600"
                    : "text-[var(--ink-muted)] hover:bg-gray-50 hover:text-[var(--ink)]"
                }`}
              >
                <Icon size={20} weight={active ? "fill" : "regular"} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="px-3 pb-4 border-t-2 pt-3" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-3 px-3 mb-3">
            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center font-bold text-xs">
              {user?.name?.charAt(0) || "A"}
            </div>
            <div className="overflow-hidden">
              <div className="text-sm font-bold truncate">{user?.name || "Admin"}</div>
              <div className="text-xs text-[var(--ink-muted)] truncate">{user?.email || ""}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-[var(--ink-muted)] hover:bg-gray-50 hover:text-red-600 w-full"
          >
            <SignOut size={20} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        <div className="h-16 flex items-center gap-4 px-5 border-b-2 bg-white sticky top-0 z-30" style={{ borderColor: "var(--border)" }}>
          <button className="lg:hidden" onClick={() => setMobileOpen(true)}>
            <List size={24} />
          </button>
          <h1 className="font-black text-xl tracking-tight">
            {NAV.find(n => n.path === loc.pathname || loc.pathname.startsWith(n.path))?.label || "Admin"}
          </h1>
        </div>
        <div className="p-5">{children}</div>
      </main>
    </div>
  );
}